import React, { useEffect, useState, useCallback } from 'react';
import { Card, Button, Spinner, Alert, Accordion } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { load } from '@cashfreepayments/cashfree-js';
import styled from 'styled-components';

const CASHFREE_MODES = {
  SANDBOX: 'sandbox',
  PRODUCTION: 'production'
};

const PaymentContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 1rem;
  background-color: #f8f9fa;
`;

const StyledCard = styled(Card)`
  border: none;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: 500px;
  padding: 2rem;
`;

const PaymentHeader = styled.h3`
  color: #4b6cb7;
  text-align: center;
  margin-bottom: 1.5rem;
  font-weight: 600;
`;

const PaymentButton = styled(Button)`
  width: 100%;
  padding: 0.75rem;
  font-weight: 500;
  font-size: 1rem;
  border-radius: 8px;
  margin-top: 1rem;
`;

const PolicyText = styled.div`
  font-size: 0.8rem;
  color: #666;
  margin-top: 1.5rem;
  text-align: center;
  
  a {
    color: #4b6cb7;
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const PriceNote = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #212529;
  text-align: center;
  margin: 1.5rem 0 0.5rem;
`;

const SuccessMessage = styled.div`
  text-align: center;
  color: #28a745;
  font-size: 1.2rem;
  margin-bottom: 1.5rem;
`;

const PaymentPage = () => {
  const { state } = useLocation();
  const navigate = useNavigate();

  const [cf, setCf] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sessionId, setSessionId] = useState('');

  const cashfreeMode = process.env.REACT_APP_CASHFREE_MODE || CASHFREE_MODES.SANDBOX;
  const isSandboxMode = cashfreeMode === CASHFREE_MODES.SANDBOX;

  const initializeCashfree = useCallback(async () => {
    try {
      setLoading(true);
      const cfInstance = await load({ mode: cashfreeMode });
      setCf(cfInstance);
    } catch (err) {
      console.error('Cashfree initialization error:', err);
      setError('Failed to initialize payment processor. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  }, [cashfreeMode]);

  const processPayment = useCallback(async () => {
    if (!cf || !sessionId) {
      setError('Payment system not ready. Please try again.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      await cf.checkout({
        paymentSessionId: sessionId,
        redirectTarget: '_self',
      });
    } catch (err) {
      console.error('Payment processing error:', err);
      setError('Failed to process payment. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [cf, sessionId]);

  useEffect(() => {
    if (!state?.sessionId) {
      setError("Missing payment information. Please try again.");
      navigate('/');
      return;
    }
    setSessionId(state.sessionId);
  }, [state, navigate]);

  useEffect(() => {
    initializeCashfree();
  }, [initializeCashfree]);

  return (
    <PaymentContainer>
      <StyledCard>
        <SuccessMessage>
          <i className="bi bi-check-circle-fill me-2"></i>
          Thank you for registering.
        </SuccessMessage>
        
        <PaymentHeader>Complete your registration by paying the fee below</PaymentHeader>
        
        {error && (
          <Alert variant="danger" className="text-center">
            <i className="bi bi-exclamation-circle me-2"></i>
            {error}
          </Alert>
        )}

        {isSandboxMode && (
          <Alert variant="warning" className="text-center">
            <i className="bi bi-info-circle me-2"></i>
            Sandbox Mode - Test payments only
          </Alert>
        )}

        <div className="text-center">
          <i className="bi bi-credit-card" style={{ fontSize: '3rem', color: '#4b6cb7' }}></i>
          <p className="mt-2 text-muted">
            {isSandboxMode 
              ? 'Test payment processed by Cashfree' 
              : 'Secure payment processed by Cashfree'}
          </p>
        </div>

        <PriceNote>Registration Fees: Rs. 2000/-</PriceNote>

        <PaymentButton 
          variant="primary" 
          onClick={processPayment}
          disabled={loading || !sessionId}
          aria-busy={loading}
          aria-live="polite"
        >
          {loading ? (
            <>
              <Spinner 
                animation="border" 
                size="sm" 
                className="me-2" 
                role="status"
                aria-hidden={!loading}
              />
              Processing...
            </>
          ) : (
            'Proceed to Payment'
          )}
        </PaymentButton>

        <Accordion className="mt-3">
          <Accordion.Item eventKey="0">
            <Accordion.Header>Return & Refund Policy</Accordion.Header>
            <Accordion.Body style={{ fontSize: '0.8rem' }}>
              <p><strong>Thank you for choosing IBM Q²D.</strong></p>
              <p>Please note that once a registration is completed, access is immediately granted to proprietary learning resources. Therefore, we urge all users to carefully review the information before proceeding, as refunds are not permitted except in the limited case outlined below.</p>
              
              <p><strong>Refund Eligibility</strong></p>
              <p>Refunds will only be considered under the following condition:</p>
              <ul>
                <li><strong>Duplicate Payment:</strong> If two payments are accidentally made for the same registration, the excess amount will be refunded.</li>
              </ul>
              
              <p>To request a refund in such cases, please email us at xxxxx@ibmq2d.in with:</p>
              <ul>
                <li>Proof of both payment transactions</li>
                <li>Name and registration details</li>
              </ul>
              
              <p>Once verified, the duplicate payment will be refunded within seven (7) working days.</p>
              
              <p><strong>Important Notes</strong></p>
              <ul>
                <li>Refund requests made for any reason other than a duplicate payment will not be entertained.</li>
                <li>All registrations are final and non-transferable.</li>
              </ul>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </StyledCard>
    </PaymentContainer>
  );
};

export default PaymentPage;